import {
  CANONICAL_CONCEPTS,
  type CanonicalConcept,
  type ResolvedTerm,
  type ResolvedTerminology,
  type TermSource,
  type TerminologyOverride,
} from '@/types/institution-experience';

/**
 * The data behind the terminology table — extracted from the page so the logic is a
 * pure function a test can hold still.
 *
 * TWO SERVER ANSWERS, ONE TABLE. The resolve endpoint says what each concept's word
 * IS and which layer supplied it; the overrides endpoint says what THIS institution
 * wrote, per locale. The table needs both, because "the word came from the
 * INSTITUTION layer" and "this institution may revert this row" are almost — but not
 * exactly — the same statement: the resolved source can be INSTITUTION while the
 * override that produced it is in a fallback locale (the school wrote an English
 * label, the reader asked in French, the locale chain served the English row). The
 * revert action must target the override that exists, not the locale on screen, or
 * DELETE returns "No INSTITUTION label ... to remove" and the row appears stuck.
 */

export interface TerminologyRow {
  concept: CanonicalConcept;
  /** The resolved word, or null when the resolver had no answer at all (never happens for 'en'). */
  term: ResolvedTerm | null;
  /** The layer the word came from — drives the source badge. */
  source: TermSource | null;
  /**
   * The institution's stored override for this concept in the locale on screen.
   * Non-null is what offers "revert to inherited" and seeds the editor with the
   * exact stored labels rather than the resolved projection of them.
   */
  override: TerminologyOverride | null;
  /**
   * True when the institution holds an override for this concept in ANY locale.
   * Shown as a hint so a French reader can tell the school customised the English
   * word — otherwise that fact is invisible until the language toggles.
   */
  overriddenElsewhere: boolean;
}

/**
 * One row per canonical concept, in the fixed concept order — the table's shape
 * never depends on which rows happen to be overridden.
 *
 * Only INSTITUTION-scope overrides count here. Campus rows belong to a campus
 * editor this screen does not host, and counting them would mark a row "yours"
 * when reverting it from this screen would not change what the institution reads.
 */
export function buildTerminologyRows(
  resolved: ResolvedTerminology,
  overrides: TerminologyOverride[],
  locale: string,
): TerminologyRow[] {
  const institutionRows = overrides.filter((row) => row.scope === 'INSTITUTION');
  return CANONICAL_CONCEPTS.map((concept) => {
    const term = resolved.terms[concept] ?? null;
    const forConcept = institutionRows.filter((row) => row.canonicalKey === concept);
    const override = forConcept.find((row) => row.locale === locale) ?? null;
    return {
      concept,
      term,
      source: term?.source ?? null,
      override,
      overriddenElsewhere: !override && forConcept.length > 0,
    };
  });
}

/**
 * Fills a preview sentence's {CONCEPT} placeholders with the resolved words.
 *
 * The sentence itself is an i18n string — "Invite a {LEARNER} to a {TEACHING_GROUP}
 * for the {ACADEMIC_PERIOD}" — so EN and FR each control their own grammar around
 * the holes. Unknown placeholders and unresolved concepts are left as the raw
 * {KEY}: a visible hole is honest, where a silently dropped word would read as a
 * grammar bug in the sentence we wrote.
 */
export function fillPreview(
  sentence: string,
  terms: Partial<Record<CanonicalConcept, ResolvedTerm>>,
  form: 'singular' | 'plural' = 'singular',
): string {
  return sentence.replace(/\{([A-Z_]+)\}/g, (whole, key: string) => {
    const term = terms[key as CanonicalConcept];
    if (!term) return whole;
    return form === 'plural' ? term.plural : term.singular;
  });
}
